/**
 * User timezone management hook
 * Provides timezone-aware date/time formatting based on user's profile timezone setting.
 *
 * This hook always uses the user's personal timezone setting (from their account profile).
 * For places that need org timezone toggle functionality, use useTimeTimezone instead.
 */

import { useAuth } from '@/store/auth.store'
import {
  DATE_FORMATS,
  getDateOnlyForDisplay,
  isDateOnlyLikeValue,
} from '@/utils/date'
import {
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  parseISO,
} from 'date-fns'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

/**
 * Get a readable timezone label from an IANA timezone string
 * e.g., "America/Los_Angeles" -> "Los Angeles"
 */
function getTimezoneLabel(timezone: string): string {
  const parts = timezone.split('/')
  const city = parts[parts.length - 1]
  return city.replace(/_/g, ' ')
}

/**
 * Get timezone abbreviation for a given timezone and date
 * e.g., "PST" or "PDT" depending on DST
 */
function getTimezoneAbbreviation(
  timezone: string,
  date: Date = new Date(),
): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
    const parts = formatter.formatToParts(date)
    const tzPart = parts.find(part => part.type === 'timeZoneName')
    return tzPart?.value || timezone
  } catch {
    return timezone
  }
}

/**
 * Parse a date input to a Date object
 */
function parseDate(date: Date | string | null | undefined): Date | null {
  if (!date) return null
  if (typeof date === 'string') {
    // Try parsing as ISO first, then as regular Date
    try {
      return parseISO(date)
    } catch {
      return new Date(date)
    }
  }
  return date
}

export interface UserTimezoneInfo {
  /** User's timezone (from profile or system fallback) */
  timezone: string
  /** Readable label for timezone */
  timezoneLabel: string
  /** Short abbreviation for timezone (e.g., PST, EST) */
  timezoneAbbr: string
}

/**
 * Hook for formatting dates/times in the user's timezone.
 * Always uses the user's personal timezone from their account settings.
 */
export function useUserTimezone() {
  const { getUserTimezone, user } = useAuth()

  // Get user's timezone with system fallback
  const timezone = getUserTimezone()

  // Get user's preferred time format (12 or 24 hour)
  const timeFormat = user?.timeFormat || '12'

  // Timezone info object
  const timezoneInfo: UserTimezoneInfo = {
    timezone,
    timezoneLabel: getTimezoneLabel(timezone),
    timezoneAbbr: getTimezoneAbbreviation(timezone),
  }

  /**
   * Format a date in the user's timezone
   */
  const formatDate = (
    date: Date | string | null | undefined,
    formatStr: string = DATE_FORMATS.display,
  ): string => {
    if (isDateOnlyLikeValue(date)) {
      const displayDate = getDateOnlyForDisplay(date)
      return displayDate ? format(displayDate, formatStr) : ''
    }

    const dateObj = parseDate(date)
    if (!dateObj) return ''

    try {
      return formatInTimeZone(dateObj, timezone, formatStr)
    } catch {
      // Fallback to standard format
      return format(dateObj, formatStr)
    }
  }

  /**
   * Format a time in the user's timezone
   */
  const formatTime = (
    date: Date | string | null | undefined,
    formatStr?: string,
  ): string => {
    const dateObj = parseDate(date)
    if (!dateObj) return ''

    // Use user's preferred time format if no custom format provided
    const defaultFormat = timeFormat === '24' ? 'HH:mm' : 'h:mm a'
    const useFormat = formatStr ?? defaultFormat

    try {
      return formatInTimeZone(dateObj, timezone, useFormat)
    } catch {
      return format(dateObj, useFormat)
    }
  }

  /**
   * Format a date and time together in the user's timezone
   */
  const formatDateTime = (
    date: Date | string | null | undefined,
    formatStr?: string,
  ): string => {
    const dateObj = parseDate(date)
    if (!dateObj) return ''

    // Default format using user preferences
    const timeFormatStr = timeFormat === '24' ? 'HH:mm' : 'h:mm a'
    const defaultFormat = `${DATE_FORMATS.display} 'at' ${timeFormatStr}`
    const useFormat = formatStr ?? defaultFormat

    try {
      return formatInTimeZone(dateObj, timezone, useFormat)
    } catch {
      return format(dateObj, useFormat)
    }
  }

  /**
   * Format a message/comment date in a chat-friendly way
   * Returns just the time (e.g., "4:32 PM") since date is shown in section headers
   */
  const formatMessageDate = (
    date: Date | string | null | undefined,
  ): string => {
    const dateObj = parseDate(date)
    if (!dateObj) return ''

    const timeFormatStr = timeFormat === '24' ? 'HH:mm' : 'h:mm a'

    try {
      return formatInTimeZone(dateObj, timezone, timeFormatStr)
    } catch {
      return format(dateObj, timeFormatStr)
    }
  }

  /**
   * Format a message date inline (compact format for chat messages)
   * Returns strings like "4:32 PM", "Yesterday, 10:15 AM", "Dec 16, 4:32 PM"
   */
  const formatMessageDateInline = (
    date: Date | string | null | undefined,
  ): string => {
    const dateObj = parseDate(date)
    if (!dateObj) return ''

    const timeFormatStr = timeFormat === '24' ? 'HH:mm' : 'h:mm a'

    try {
      // Convert to user's timezone for comparison
      const zonedDate = utcToZonedTime(dateObj, timezone)
      const now = utcToZonedTime(new Date(), timezone)

      // Check if same day in user's timezone
      const isSameDay =
        zonedDate.getFullYear() === now.getFullYear() &&
        zonedDate.getMonth() === now.getMonth() &&
        zonedDate.getDate() === now.getDate()

      // Check if yesterday in user's timezone
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      const isYesterdayDate =
        zonedDate.getFullYear() === yesterday.getFullYear() &&
        zonedDate.getMonth() === yesterday.getMonth() &&
        zonedDate.getDate() === yesterday.getDate()

      if (isSameDay) {
        return formatInTimeZone(dateObj, timezone, timeFormatStr)
      } else if (isYesterdayDate) {
        return `Yesterday, ${formatInTimeZone(dateObj, timezone, timeFormatStr)}`
      } else {
        return formatInTimeZone(dateObj, timezone, `MMM d, ${timeFormatStr}`)
      }
    } catch {
      // Fallback without timezone conversion
      if (isToday(dateObj)) {
        return format(dateObj, timeFormatStr)
      } else if (isYesterday(dateObj)) {
        return `Yesterday, ${format(dateObj, timeFormatStr)}`
      } else {
        return format(dateObj, `MMM d, ${timeFormatStr}`)
      }
    }
  }

  /**
   * Format a date section header (for grouping messages by date)
   * Returns strings like "Today", "Yesterday", "Monday, December 16, 2025"
   */
  const formatDateSection = (
    date: Date | string | null | undefined,
  ): string => {
    const dateObj = parseDate(date)
    if (!dateObj) return ''

    try {
      // Convert to user's timezone for comparison
      const zonedDate = utcToZonedTime(dateObj, timezone)
      const now = utcToZonedTime(new Date(), timezone)

      // Check if same day in user's timezone
      const isSameDay =
        zonedDate.getFullYear() === now.getFullYear() &&
        zonedDate.getMonth() === now.getMonth() &&
        zonedDate.getDate() === now.getDate()

      // Check if yesterday in user's timezone
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      const isYesterdayDate =
        zonedDate.getFullYear() === yesterday.getFullYear() &&
        zonedDate.getMonth() === yesterday.getMonth() &&
        zonedDate.getDate() === yesterday.getDate()

      if (isSameDay) {
        return 'Today'
      } else if (isYesterdayDate) {
        return 'Yesterday'
      } else {
        return formatInTimeZone(dateObj, timezone, 'EEEE, MMMM d, yyyy')
      }
    } catch {
      // Fallback without timezone conversion
      if (isToday(dateObj)) {
        return 'Today'
      } else if (isYesterday(dateObj)) {
        return 'Yesterday'
      } else {
        return format(dateObj, 'EEEE, MMMM d, yyyy')
      }
    }
  }

  /**
   * Format relative time (e.g., "2 hours ago", "5 minutes ago")
   * Note: This is approximate and doesn't account for timezone differences
   */
  const formatRelativeTime = (
    date: Date | string | null | undefined,
    options?: { addSuffix?: boolean },
  ): string => {
    const dateObj = parseDate(date)
    if (!dateObj) return ''

    return formatDistanceToNow(dateObj, {
      addSuffix: options?.addSuffix ?? false,
    })
  }

  /**
   * Get the current time in the user's timezone
   */
  const getCurrentTime = (): Date => {
    return utcToZonedTime(new Date(), timezone)
  }

  /**
   * Convert a UTC date to the user's timezone
   */
  const toUserTimezone = (
    date: Date | string | null | undefined,
  ): Date | null => {
    const dateObj = parseDate(date)
    if (!dateObj) return null
    return utcToZonedTime(dateObj, timezone)
  }

  /**
   * Convert a date from user's timezone back to UTC for storage
   */
  const fromUserTimezone = (
    date: Date | string | null | undefined,
  ): Date | null => {
    const dateObj = parseDate(date)
    if (!dateObj) return null
    return zonedTimeToUtc(dateObj, timezone)
  }

  /**
   * Get a date key for grouping (in user's timezone)
   * Returns format: 'yyyy-MM-dd'
   */
  const getDateKey = (date: Date | string | null | undefined): string => {
    if (isDateOnlyLikeValue(date)) {
      const displayDate = getDateOnlyForDisplay(date)
      return displayDate ? format(displayDate, 'yyyy-MM-dd') : ''
    }

    const dateObj = parseDate(date)
    if (!dateObj) return ''

    try {
      return formatInTimeZone(dateObj, timezone, 'yyyy-MM-dd')
    } catch {
      return format(dateObj, 'yyyy-MM-dd')
    }
  }

  /**
   * Check if a date is today in the user's timezone
   */
  const isTodayInTimezone = (
    date: Date | string | null | undefined,
  ): boolean => {
    const dateObj = parseDate(date)
    if (!dateObj) return false

    try {
      const zonedDate = utcToZonedTime(dateObj, timezone)
      const now = utcToZonedTime(new Date(), timezone)

      return (
        zonedDate.getFullYear() === now.getFullYear() &&
        zonedDate.getMonth() === now.getMonth() &&
        zonedDate.getDate() === now.getDate()
      )
    } catch {
      return isToday(dateObj)
    }
  }

  /**
   * Check if a date is yesterday in the user's timezone
   */
  const isYesterdayInTimezone = (
    date: Date | string | null | undefined,
  ): boolean => {
    const dateObj = parseDate(date)
    if (!dateObj) return false

    try {
      const zonedDate = utcToZonedTime(dateObj, timezone)
      const now = utcToZonedTime(new Date(), timezone)
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)

      return (
        zonedDate.getFullYear() === yesterday.getFullYear() &&
        zonedDate.getMonth() === yesterday.getMonth() &&
        zonedDate.getDate() === yesterday.getDate()
      )
    } catch {
      return isYesterday(dateObj)
    }
  }

  /**
   * Check if a date is in the past in the user's timezone
   */
  const isPastInTimezone = (
    date: Date | string | null | undefined,
  ): boolean => {
    const dateObj = parseDate(date)
    if (!dateObj) return false

    try {
      const zonedDate = utcToZonedTime(dateObj, timezone)
      const now = utcToZonedTime(new Date(), timezone)

      // Compare at start of day for date-only comparisons
      const zonedDay = new Date(
        zonedDate.getFullYear(),
        zonedDate.getMonth(),
        zonedDate.getDate(),
      )
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

      return zonedDay < today
    } catch {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const compareDate = new Date(dateObj)
      compareDate.setHours(0, 0, 0, 0)
      return compareDate < today
    }
  }

  /**
   * Check if a date is tomorrow in the user's timezone
   */
  const isTomorrowInTimezone = (
    date: Date | string | null | undefined,
  ): boolean => {
    const dateObj = parseDate(date)
    if (!dateObj) return false

    try {
      const zonedDate = utcToZonedTime(dateObj, timezone)
      const now = utcToZonedTime(new Date(), timezone)
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)

      return (
        zonedDate.getFullYear() === tomorrow.getFullYear() &&
        zonedDate.getMonth() === tomorrow.getMonth() &&
        zonedDate.getDate() === tomorrow.getDate()
      )
    } catch {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return (
        dateObj.getFullYear() === tomorrow.getFullYear() &&
        dateObj.getMonth() === tomorrow.getMonth() &&
        dateObj.getDate() === tomorrow.getDate()
      )
    }
  }

  /**
   * Get the difference in days between a date and today in the user's timezone
   */
  const differenceInDaysFromToday = (
    date: Date | string | null | undefined,
  ): number => {
    const dateObj = parseDate(date)
    if (!dateObj) return 0

    try {
      const zonedDate = utcToZonedTime(dateObj, timezone)
      const now = utcToZonedTime(new Date(), timezone)

      // Compare at start of day
      const zonedDay = new Date(
        zonedDate.getFullYear(),
        zonedDate.getMonth(),
        zonedDate.getDate(),
      )
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

      const diffTime = zonedDay.getTime() - today.getTime()
      return Math.floor(diffTime / (1000 * 60 * 60 * 24))
    } catch {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const compareDate = new Date(dateObj)
      compareDate.setHours(0, 0, 0, 0)
      const diffTime = compareDate.getTime() - today.getTime()
      return Math.floor(diffTime / (1000 * 60 * 60 * 24))
    }
  }

  return {
    // Timezone info
    timezone,
    ...timezoneInfo,
    timezoneInfo,

    // Formatting utilities
    formatDate,
    formatTime,
    formatDateTime,
    formatMessageDate,
    formatMessageDateInline,
    formatDateSection,
    formatRelativeTime,
    getDateKey,

    // Date comparison utilities (timezone-aware)
    isTodayInTimezone,
    isYesterdayInTimezone,
    isPastInTimezone,
    isTomorrowInTimezone,
    differenceInDaysFromToday,

    // Timezone conversion utilities
    getCurrentTime,
    toUserTimezone,
    fromUserTimezone,

    // Static utilities
    getTimezoneLabel,
    getTimezoneAbbreviation,
  }
}

export type UseUserTimezoneReturn = ReturnType<typeof useUserTimezone>
